/**
 * Optimistic create + delete mutations for the conversation sidebar.
 *
 * v1 references:
 *   - `ConversationContext.addConversation()` lines 214-259
 *   - `ConversationContext.removeConversation()` lines 387-413
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  deleteConversation,
  updateConversation,
  type UpdateConversationInput,
} from "@ando/core/api";

import { useProjectsStore } from "@/features/projects/state/projectsStore.js";
import { useChatStore } from "../state/chatStore.js";
import type { ChatConversation } from "../state/types.js";
import { chatConversationsQueryKey } from "./useConversationsQuery.js";
import { useChatUserId } from "./useChatSession.js";

function localId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `conv-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

export interface UseCreateConversationResult {
  /** Triggers optimistic insert. Returns the new conversation id. */
  create: (title?: string) => string;
}

/**
 * v1 didn't hit a REST endpoint on create — the first WS query implicitly
 * registered the conversation tree on the BE. Mirror that contract: insert
 * locally and let the next outbound query envelope register it. The mutation
 * is purely client-state for now; if a real `POST /conversations` lands
 * later, swap the body of `create` for a TanStack mutation.
 */
export function useCreateConversation(): UseCreateConversationResult {
  const userId = useChatUserId();
  const upsert = useChatStore((s) => s.upsertConversation);
  const select = useChatStore((s) => s.selectConversation);
  const markLocallyCreated = useChatStore((s) => s.markLocallyCreated);

  return {
    create: (title) => {
      const id = localId();
      // Inherit the currently-selected project so the new chat lands in
      // the right sidebar bucket immediately, instead of flashing under
      // "Senza progetto" until the first WS query echoes the project_id
      // back through history replay. Read via ``.getState()`` (not a
      // hook) so the call stays synchronous and a stale ``create()`` ref
      // captured in a callback still picks up the LATEST selection at
      // dispatch time.
      const currentProjectId = useProjectsStore.getState().currentProjectId;
      const next: ChatConversation = {
        id,
        title: title ?? "",
        user_id: userId ?? "",
        queries: [],
        updated_at: new Date().toISOString(),
        config: {
          mode: "fast",
          webSearchMode: false,
          focus: "standard",
          selectedConnectionIds: [],
          agentModeVersionId: null,
          folderFilter: [],
          scopedDocuments: [],
          connectSpace: null,
          terraMode: false,
        },
        replayInProgress: false,
        project_id: currentProjectId,
      };
      upsert(next);
      markLocallyCreated(id);
      select(id);
      return id;
    },
  };
}

export interface UseDeleteConversationResult {
  remove: (id: string) => Promise<void>;
  isPending: boolean;
}

export function useDeleteConversation(): UseDeleteConversationResult {
  const userId = useChatUserId();
  const removeLocal = useChatStore((s) => s.removeConversation);
  const upsertLocal = useChatStore((s) => s.upsertConversation);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (id: string) => {
      if (!userId) throw new Error("useDeleteConversation: no user session");
      return deleteConversation(userId, id);
    },
    onMutate: async (id) => {
      // Snapshot the entry for rollback.
      const existing = useChatStore.getState().conversations[id];
      removeLocal(id);
      return { existing };
    },
    onError: (_err, _id, context) => {
      // Rollback the optimistic remove.
      if (context?.existing) upsertLocal(context.existing);
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: chatConversationsQueryKey(userId),
      });
    },
  });

  return {
    remove: (id) => mutation.mutateAsync(id).then(() => undefined),
    isPending: mutation.isPending,
  };
}

export interface UseUpdateConversationResult {
  /** Persist a conversation patch (title / summary / folder filter). */
  update: (id: string, patch: UpdateConversationInput) => Promise<void>;
  /** Pending state for the latest in-flight call. */
  isPending: boolean;
}

/**
 * PATCH ``/conversations/{user_id}/{conversation_id}`` — used by the
 * sidebar "Rinomina" row action. Previously the rename was a pure
 * Zustand mutation that vanished on refresh (FE-only). This hook
 * persists the patch BE-side AND mirrors it into the chat store
 * optimistically so the row updates immediately, with rollback on
 * failure (same shape as ``useDeleteConversation``).
 */
export function useUpdateConversation(): UseUpdateConversationResult {
  const userId = useChatUserId();
  const setTitleLocal = useChatStore((s) => s.setTitle);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: UpdateConversationInput }) => {
      if (!userId) throw new Error("useUpdateConversation: no user session");
      return updateConversation(userId, id, patch);
    },
    onMutate: async ({ id, patch }) => {
      const previous = useChatStore.getState().conversations[id];
      if (patch.title !== undefined) setTitleLocal(id, patch.title);
      return { previousTitle: previous?.title };
    },
    onError: (_err, { id }, context) => {
      // Rollback the optimistic title change so the row reflects the
      // BE truth when the call fails (offline / 4xx / 5xx).
      if (context?.previousTitle !== undefined) {
        setTitleLocal(id, context.previousTitle);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: chatConversationsQueryKey(userId),
      });
    },
  });

  return {
    update: (id, patch) =>
      mutation.mutateAsync({ id, patch }).then(() => undefined),
    isPending: mutation.isPending,
  };
}
